import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ITagOnceSettings } from 'tag/ts/tag/interfaces/model/ITagOnceSettings';
import { ITagOnceSettingsManager } from 'tag/ts/tag/interfaces/model/ITagOnceSettingsManager';
import { TagOnceSettingsProperties } from 'tag/ts/tag/model/enums/TagOnceSettingsProperties';

/**
 * @description The TagOnceSettingsManager class is responsible for managing settings that are intended
 * to be retrieved and used only once.
 *
 * This class extends the AbstractTagClass and implements the ITagOnceSettingsManager interface.
 * It allows for storage, retrieval, and one-time-use access of settings.
 *
 * @copyright © Paramount Streaming
 * @class
 * @extends AbstractTagClass
 * @implements ITagOnceSettingsManager
 */
export class TagOnceSettingsManager extends AbstractTagClass implements ITagOnceSettingsManager {

	/**
	 * @description Placeholder for tag once settings
	 * @type {ITagOnceSettings}
	 * @private
	 */
	#onceSettings: ITagOnceSettings = {};

	/**
	 * @description TagOnceSettingsManager constructor
	 * @param {ITagAbstractClassArgument} argument
	 */
	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'TagOnceSettingsManager'
			}
		);
	}

	/**
	 * @description Returns once settings object
	 * @returns {ITagOnceSettings}
	 */
	public get onceSettings(): ITagOnceSettings {
		return this.#onceSettings;
	}

	/**
	 * @description Sets once settings object
	 * @param {ITagOnceSettings} settings
	 */
	public set onceSettings( settings: ITagOnceSettings ) {
		this.#onceSettings = settings;
	}

	/**
	 * @description Returns once settings value and deletes it from the object
	 * @param {string | TagOnceSettingsProperties} key
	 * @returns {unknown}
	 */
	public getOnceSettings( key: string | TagOnceSettingsProperties = undefined ): unknown {
		// check if key is not empty and is a string
		if ( this.utils.Type.isEmpty( key ) || !this.utils.Type.isString( key ) ) {
			return undefined;
		}
		// get value from onceSettings object
		const value = this?.onceSettings?.[ key ];
		// delete a property key from onceSettings object
		delete this?.onceSettings?.[ key ];
		// return value
		return value;
	}

}